import { useEffect, useState } from 'react';
import {
  CardContent,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useApplicationPdfStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { DateUtils, LocalStorageUtils, PermissionsUtils, RoutePaths } from '@shared/utils';
import type { ApplicationItem, Partner } from '@shared/api';
import { ApplicationType, PermissionLevel, PermissionType } from '@shared/types';
import { PDFView } from '@widgets';
import { DelayedLinearProgress, RouterNavLink } from '@shared/widgets';

interface ApplicationsTableProps {
  loading: boolean;
  data: ApplicationItem[];
}

const getApplicationId = (row: ApplicationItem) => String(row.appId || row.id);

export const ApplicationsTable = ({ loading, data }: ApplicationsTableProps) => {
  const { navigate } = useRouteHook();

  const { loading: loadingPdf, data: file, getApplicationPdf, clear: clearApplicationPdf } = useApplicationPdfStore();

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<ApplicationItem | null>(null);

  useEffect(() => {
    setPartners(LocalStorageUtils.getPartners() || []);
  }, []);

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, row: ApplicationItem) => {
    setMenuAnchor(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  const openPdf = (applicationId: string, applicationType: string) => getApplicationPdf({ applicationId, applicationType });

  const columnsCount = partners && partners.length > 1 ? 6 : 5;

  const routeToDetails = (appId: string, applicationType: string, mainAppId: string | undefined) =>
    applicationType === ApplicationType.MAIN && !mainAppId
      ? RoutePaths.applicationsDetails.onNavigate(appId)
      : RoutePaths.applicationsSupplementaryDetails.onNavigate(mainAppId!, appId);

  const routeToEdit = (appId: string, applicationType: string, mainAppId: string | undefined) => {
    applicationType === ApplicationType.MAIN
      ? navigate(RoutePaths.applicationsEdit.onNavigate(appId))
      : navigate(RoutePaths.applicationsSupplementaryEdit.onNavigate(mainAppId!, appId), { state: { suppId: appId } });
  };

  const handleView = (row: ApplicationItem) => {
    navigate(routeToDetails(getApplicationId(row), row.applicationType, row.mainAppId));
    handleCloseMenu();
  };

  const handleEdit = (row: ApplicationItem) => {
    routeToEdit(getApplicationId(row), row.applicationType, row.mainAppId);
    handleCloseMenu();
  };

  const handlePrint = (row: ApplicationItem) => {
    openPdf(getApplicationId(row), row.applicationType);
    handleCloseMenu();
  };

  if (loading)
    return (
      <CardContent style={{ textAlign: 'center' }}>
        <Typography variant='body1'>Loading application details</Typography>
      </CardContent>
    );
  if (!data.length) return <CardContent style={{ textAlign: 'center' }}>No data to display.</CardContent>;
  return (
    <>
      <DelayedLinearProgress loading={loadingPdf} />
      <Table aria-label='simple table' stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: '220px' }} align='left'>
              Application Ref №
            </TableCell>
            <TableCell style={{ width: '220px' }} align='left'>
              Full Name
            </TableCell>
            <TableCell style={{ width: '220px' }} align='left'>
              Passport/ID
            </TableCell>
            {partners && partners.length > 1 && (
              <TableCell style={{ width: '180px' }} align='center'>
                Partner
              </TableCell>
            )}
            <TableCell style={{ width: '150px' }} align='center'>
              Product
            </TableCell>
            <TableCell style={{ width: '180px' }} align='center'>
              Status
            </TableCell>
            <TableCell style={{ width: '160px' }} align='center'>
              Last Update
            </TableCell>
            <TableCell style={{ width: '170px' }} align='center'>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
            const applicationId = getApplicationId(row);
            return (
              <TableRow key={applicationId} hover>
                <TableCell align='left' component='th' scope='row'>
                  <Link component={RouterNavLink} to={routeToDetails(applicationId, row.applicationType, row.mainAppId)} color='primary'>
                    {row.name}
                  </Link>
                </TableCell>
                <TableCell>
                  {row.firstName} {row.lastName}
                </TableCell>
                <TableCell>{row.passport_IDCardNo}</TableCell>
                {partners && partners.length > 1 && <TableCell align='center'>{row.partner}</TableCell>}
                <TableCell align='center'>
                  {row.product || row.productName}
                  {row.applicationType === ApplicationType.SUPPLEMENTARY ? ' Supplementary' : null}
                </TableCell>
                <TableCell align='center'>{row.status}</TableCell>
                <TableCell align='center'>{DateUtils.formatDate(row.processedDate)}</TableCell>
                <TableCell align='center'>
                  <IconButton onClick={(e) => handleOpenMenu(e, row)} size='large'>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {!data.length ? (
            <TableRow>
              <TableCell colSpan={columnsCount} align='center'>
                No Records Found
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        {selectedRow && (
          <>
            <MenuItem onClick={() => handleView(selectedRow)}>View</MenuItem>
            {PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.EDIT) && selectedRow.status === 'Draft' && (
              <MenuItem onClick={() => handleEdit(selectedRow)}>Edit</MenuItem>
            )}
            <MenuItem disabled={loadingPdf} onClick={() => handlePrint(selectedRow)}>
              Print
            </MenuItem>
          </>
        )}
      </Menu>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component='div'
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        onRowsPerPageChange={(e) => {
          setPage(0);
          setRowsPerPage(+e.target.value);
        }}
      />
      {file && <PDFView objectUrl={URL.createObjectURL(file)} onClose={clearApplicationPdf} />}
    </>
  );
};
